import {HttpClient} from '@angular/common/http';
import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {DialogService} from '@app/services/dialog.service';
import {GtNotificationService} from '@app/services/gt-notification.service';
import {GtPageSizeService} from '@app/services/gt-page-size.service';
import {MediaFileControllerService} from '@app/services/media-file-controller.service';
import {MediaFileStoreModel} from '@app/services/media-file-store.service';
import {EMPTY, of, Subject} from 'rxjs';
import {catchError, filter, switchMap, takeUntil, tap} from 'rxjs/operators';
import {AnnouncementsTableDialogComponent} from './announcements-table-dialog.component';

@Component({
  selector: 'gt-announcements-table',
  templateUrl: './announcements-table.component.html',
  styleUrls: ['./announcements-table.component.scss']
})
export class AnnouncementsTableComponent implements OnInit, OnDestroy {
  @Input() global = true;
  @Input() tableName = 'announcements-table';
  @ViewChild(MatSort, {static: true}) sort: MatSort;
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;

  dataSource = new MatTableDataSource<MediaFileStoreModel>();
  displayedColumns = ['displayName', 'description', 'actions'];
  playing$ = this.mediaFileControllerService.playing$;

  private dialogWidth = 700;
  private destroyed$ = new Subject<void>();

  constructor(
    private mediaFileControllerService: MediaFileControllerService,
    private httpClient: HttpClient,
    private dialog: MatDialog,
    private pageSizeService: GtPageSizeService,
    private dialogService: DialogService,
    private notificationService: GtNotificationService
  ) {}

  ngOnInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    const mediaFiles$ = this.global ? this.mediaFileControllerService.global$ : this.mediaFileControllerService.custom$;
    mediaFiles$
      .pipe(
        tap(mediaFiles => (this.dataSource.data = mediaFiles)),
        catchError(error => {
          this.dataSource.data = [];
          return of();
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe();
    this.paginator.pageSize = this.pageSizeService.getPageSize(this.tableName);
  }

  ngOnDestroy() {
    this.stop();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  openDeleteDialog(mediaFile: MediaFileStoreModel) {
    this.dialogService
      .deleteConfirmationDialog(mediaFile.displayName)
      .pipe(
        filter(ok => !!ok),
        switchMap(() => this.mediaFileControllerService.delete(mediaFile.id)),
        catchError(error => {
          this.notificationService.customError(error);
          return EMPTY;
        })
      )
      .subscribe();
  }

  openAnnouncementDialog(mediaFile?: MediaFileStoreModel) {
    this.dialog.open(AnnouncementsTableDialogComponent, {
      minWidth: this.dialogWidth,
      data: mediaFile,
      disableClose: true
    });
  }

  play(mediaFile: MediaFileStoreModel) {
    this.mediaFileControllerService.play(mediaFile.fileName);
  }

  stop() {
    this.mediaFileControllerService.stop();
  }

  download(mediaFile: MediaFileStoreModel) {
    this.mediaFileControllerService
      .getDownloadLocation(mediaFile.fileName)
      .pipe(
        switchMap(downloadLocation => this.httpClient.get(downloadLocation, {responseType: 'blob'})),
        tap(response => {
          const downloadLink = document.createElement('a');
          downloadLink.href = URL.createObjectURL(response);
          downloadLink.setAttribute('download', mediaFile.fileName.split('/').pop());
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
        }),
        catchError(error => {
          this.notificationService.customError(error);
          return EMPTY;
        })
      )
      .subscribe();
  }

  paging(pageEvent: PageEvent) {
    this.pageSizeService.setPageSize(this.tableName, pageEvent.pageSize);
  }
}
